import { Button, IconButton, Typography, alpha } from "@mui/material";
import { Filter, FilterData } from "./filters/Filter";
import { dayOfWeekOptions, timeOfDayOptions } from "./filters/timeFilterOptions";
import { useRef, useState } from "react";
import Drawer from "@mui/material/Drawer";
import makeStyles from "@mui/styles/makeStyles";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import TuneIcon from "@mui/icons-material/Tune";
import { NAVBAR_HEIGHT, NAVBAR_HEIGHT_MOBILE } from "app/styles";
import { useDetectScroll } from "hooks/useDetectScroll";
import { useElementPosition } from "hooks/useElementPosition";
import { useQuery } from "@apollo/client";
import { InterestGroupTopic } from "@app/shared/types";
import { GRAPHQL_QUERY_INTEREST_GROUP_TOPICS } from "app/queries";
import LoadingBar from "components/LoadingBar";
import Hidden from "@mui/material/Hidden";

type InteresGroupFilterProps = {
    filters: FilterData;
    setFilters: (filters: FilterData) => void;
    groupCount?: number;
    resetFilters: VoidFunction;
};

const useStyles = makeStyles((theme) => ({
    paper: {
        width: "40%",
        backgroundColor: theme.palette.coral100,
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(6),
        [theme.breakpoints.down("lg")]: {
            width: "100%",
            paddingLeft: theme.spacing(12),
            paddingRight: theme.spacing(12),
        },
        [theme.breakpoints.down("md")]: {
            paddingLeft: theme.spacing(6),
            paddingRight: theme.spacing(6),
        },
    },
    backButton: {
        height: theme.spacing(5),
        width: theme.spacing(5),
        marginTop: theme.spacing(2),
    },
    backButtonIcon: {
        fontSize: theme.typography.pxToRem(30),
    },
    noMargin: {
        margin: 0,
    },
    filterButton: {
        height: theme.spacing(6),
        top: -70,
    },
    filterBarButton: {
        color: theme.palette.grey700,
    },
    filterIcon: {
        marginRight: theme.spacing(1),
    },
    buttonContainer: {
        display: "flex",
        justifyContent: "flex-end",
        height: 0,
    },
    filterBar: {
        opacity: 0,
        pointerEvents: "none",

        position: "fixed",
        transition: "all 0.3s ease-in-out",
        top: NAVBAR_HEIGHT - 8,
        backgroundColor: theme.palette.coral300,
        width: "100%",
        left: 0,
        height: theme.spacing(6),
        boxShadow: theme.shadow.darker,
        zIndex: theme.zIndex.appBar,
        [theme.breakpoints.down("lg")]: {
            top: NAVBAR_HEIGHT_MOBILE - 8,
        },
    },
    filterBarWrapper: {
        maxWidth: "1272px",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        margin: "0 auto",
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(6),
        [theme.breakpoints.down("lg")]: {
            maxWidth: "100%",
        },
        [theme.breakpoints.down("md")]: {
            paddingLeft: theme.spacing(3),
            paddingRight: theme.spacing(3),
        },
    },
    fullOpacity: {
        opacity: 1,
        pointerEvents: "auto",
    },
    filterDrawerHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: theme.spacing(2),
    },
    filterDrawerTopBar: {
        position: "sticky",
        top: 0,
        zIndex: theme.zIndex.appBar,
        backgroundColor: theme.palette.coral100,
        marginBottom: theme.spacing(2),
        boxShadow: theme.shadow.default,
        // adjusting to the padding of the parent element
        marginLeft: theme.spacing(-6),
        marginRight: theme.spacing(-6),
        paddingLeft: theme.spacing(6),
        paddingRight: theme.spacing(6),
        [theme.breakpoints.down("lg")]: {
            marginLeft: theme.spacing(-12),
            marginRight: theme.spacing(-12),
        },
        [theme.breakpoints.down("md")]: {
            marginLeft: theme.spacing(-6),
            marginRight: theme.spacing(-6),
        },
    },
}));

export const InterestGroupFilter = (props: InteresGroupFilterProps) => {
    const { filters, setFilters, groupCount, resetFilters } = props;

    const classes = useStyles();
    const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);

    const filterButtonRef = useRef(null);
    const filterButtonPosition = useElementPosition(filterButtonRef);

    const numFiltersApplied =
        filters.topic.length + filters.timeOfDay.length + filters.dayOfWeek.length;

    const showFilterBar = useDetectScroll({
        threshold: filterButtonPosition,
        hideOnScrollUp: true,
    });

    const handleBackButtonClick = () => {
        setIsFilterMenuOpen(false);
    };

    const { data: topicsData, loading: topicsLoading } = useQuery(
        GRAPHQL_QUERY_INTEREST_GROUP_TOPICS,
    );

    if (topicsLoading) {
        return <LoadingBar />;
    }
    const topics = topicsData.interestGroupTopics as InterestGroupTopic[];
    const topicOptions = topics.map((x) => ({
        value: x.id,
        label: x.name,
    }));

    const renderTopicFilter = () => (
        <Filter
            options={topicOptions}
            selected={filters.topic}
            onUpdate={(topic) => setFilters({ ...filters, topic })}
            title="Show groups about"
            defaultOptionText="Anything"
            shortenLabelWhenMultipleSelected="topics"
            hideEmptyOption={true}
        />
    );

    const renderTimeFilter = () => (
        <Filter
            options={timeOfDayOptions}
            selected={filters.timeOfDay}
            onUpdate={(timeOfDay) => setFilters({ ...filters, timeOfDay })}
            title="Show groups that meet at"
            defaultOptionText={"Any time"}
            hideEmptyOption={true}
        />
    );

    const renderDayFilter = () => (
        <Filter
            options={dayOfWeekOptions}
            selected={filters.dayOfWeek}
            onUpdate={(dayOfWeek) => setFilters({ ...filters, dayOfWeek })}
            title="Show groups that meet on"
            defaultOptionText={"Any day"}
            shortenLabelWhenMultipleSelected="days"
            hideEmptyOption={true}
        />
    );

    return (
        <>
            <div className={`${classes.filterBar} ${showFilterBar && classes.fullOpacity}`}>
                <div className={classes.filterBarWrapper}>
                    <span>
                        {groupCount} {groupCount === 1 ? "group" : "groups"} found
                    </span>
                    <Button
                        onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
                        className={classes.filterBarButton}
                    >
                        <TuneIcon className={classes.filterIcon} /> Filter ({numFiltersApplied})
                    </Button>
                </div>
            </div>

            <div className={classes.buttonContainer}>
                <Button
                    onClick={() => setIsFilterMenuOpen(!isFilterMenuOpen)}
                    variant="secondary"
                    className={classes.filterButton}
                    ref={filterButtonRef}
                >
                    <TuneIcon className={classes.filterIcon} />{" "}
                    <Hidden smDown={true}>Filter ({numFiltersApplied})</Hidden>
                </Button>
            </div>

            <Drawer
                anchor="right"
                open={isFilterMenuOpen}
                onClose={() => setIsFilterMenuOpen(false)}
                classes={{ paper: classes.paper }}
            >
                <div className={classes.filterDrawerTopBar}>
                    <IconButton
                        onClick={handleBackButtonClick}
                        aria-label="go back"
                        className={classes.backButton}
                    >
                        <ArrowBackIcon className={classes.backButtonIcon} />
                    </IconButton>

                    <div className={classes.filterDrawerHeader}>
                        <Typography variant="h4" className={classes.noMargin}>
                            Filter ({numFiltersApplied})
                        </Typography>
                        {numFiltersApplied > 0 && (
                            <Button variant="tertiary" onClick={() => resetFilters()}>Clear</Button>
                        )}
                    </div>
                </div>
                <div>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Topic</Typography>
                        </AccordionSummary>
                        <AccordionDetails>{renderTopicFilter()}</AccordionDetails>
                    </Accordion>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Day of the week</Typography>
                        </AccordionSummary>
                        <AccordionDetails>{renderDayFilter()}</AccordionDetails>
                    </Accordion>
                    <Accordion>
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Typography>Period of the day</Typography>
                        </AccordionSummary>
                        <AccordionDetails>{renderTimeFilter()}</AccordionDetails>
                    </Accordion>
                </div>
            </Drawer>
        </>
    );
};
